import React, { useRef, forwardRef, useState, useEffect } from 'react';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import { parse } from 'query-string';
import { ButtonWithIcon } from '@els/els-react--button';
import { generateTraceHeaders } from '@Services/CorrelationId';
import { LLS_RESPONSE_LOCATION_VALUE, elsevierExternalUrlStrings } from '@Components/Results/constants';
import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import FullTextDialog from '@Components/Results/reactions/reaction-details/FullTextDialog';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import {
  setFullTextDocumentId as hopintoSetFullTextDocumentId,
  getFullTextDocumentId as hopintoGetFullTextDocumentId,
} from '@Containers/Hopinto/Services/FullTextResults';
import { getAnalyticsForFullTextLink } from '@Components/Results/analytics/fullTextAnalytics';
import { getLinkIndex } from '@Components/Results/analytics/citationLinksTracking';

import './FullText.scss';

const FullText = forwardRef(({ citation, trackingAttributes, metadata }, ref) => {
  const [isModalShown, toggleModalShown] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [links, setLinks] = useState(null);
  const [fullTextIndex, setFullTextIndex] = useState('0');
  const fullTextButtonRef = useRef();

  useEffect(() => {
    const hopintoFullTextDocumentId = hopintoGetFullTextDocumentId();
    if (hopintoFullTextDocumentId && hopintoFullTextDocumentId === citation.id) {
      // eslint-disable-next-line no-use-before-define
      handleLinkClick();
      hopintoSetFullTextDocumentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-shadow
  const handleResult = (result, fullTextIndex) => {
    getAnalyticsForFullTextLink({
      trackingAttributes,
      citation,
      linksObject: result,
      fullTextIndex,
      metadata,
      buttonElement: fullTextButtonRef.current,
    });
    if (result) {
      const linksKeys = Object.keys(result);
      if (linksKeys.length === 1) {
        const currentLinkKey = linksKeys[0];
        window.open(
          result[currentLinkKey] &&
            window.s &&
            window.s.visitor &&
            elsevierExternalUrlStrings.some((substring) => result[currentLinkKey]?.includes(substring))
            ? window.s?.visitor?.appendVisitorIDsTo(result[currentLinkKey])
            : result[currentLinkKey],
          '_blank'
        );
        return;
      }
    }
    toggleModalShown(true);
  };

  const getLinksFromLLSResponse = ({ valid, responses } = {}) => {
    if (!valid) {
      return {};
    }

    if (!isUndefined(responses[LLS_RESPONSE_LOCATION_VALUE])) {
      return { [LLS_RESPONSE_LOCATION_VALUE]: responses[LLS_RESPONSE_LOCATION_VALUE] };
    }

    return responses;
  };

  const handleLinkClick = async (event) => {
    // eslint-disable-next-line no-shadow
    const fullTextIndex = getLinkIndex(event && event.currentTarget);
    setFullTextIndex(fullTextIndex);
    if (isSending) {
      return;
    }

    if (links) {
      handleResult(links, fullTextIndex);
      return;
    }

    setIsSending(true);
    try {
      // The problem with using MessageBroker and axios: browser blocks popup (window.open) after axios request for some reason...
      const response = await fetch(citation.fullTextUrl, {
        method: 'GET',
        headers: {
          ...generateTraceHeaders(),
        },
      });
      const data = await response.json();
      // eslint-disable-next-line no-shadow
      const links = getLinksFromLLSResponse(data);
      setLinks(links);
      handleResult(links, fullTextIndex);
    } catch (error) {
      Logger.error(error);
      handleResult(links, fullTextIndex);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => toggleModalShown(false);

  const handleSuccess = () => {
    showCopiedToastMessage();
  };

  if (!citation.fullTextUrl) {
    return null;
  }

  const searchQuery = citation.fullTextUrl?.split('?')[1];
  const params = parse(searchQuery);

  const type = get(citation, 'type', '');
  return (
    <>
      <RsModal showBackdrop showModal={isModalShown} className="full-text-dialogs-root">
        <FullTextDialog
          onClose={handleClose}
          onCopy={handleSuccess}
          isPatent={type.toLowerCase()?.includes('patent')}
          fields={params}
          links={links}
          citation={citation}
          trackingAttributes={trackingAttributes}
          fullTextIndex={+fullTextIndex}
          metadata={metadata}
          buttonElement={fullTextButtonRef.current}
        />
      </RsModal>
      <ButtonWithIcon
        ref={ref}
        type="link"
        sprite="UpRight"
        iconRight
        data-e2e="e2e-full-text-button"
        linkIconSize="xs"
        className="e2e-full-text rx-no-wrap u-els-font-size-base"
        onClick={handleLinkClick}>
        Full Text
      </ButtonWithIcon>
    </>
  );
});

FullText.displayName = 'FullText';

export default FullText;
